import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import './i18n/config'
import MagicalHome from './components/home/MagicalHome'
import PlayerSetup from './components/game/PlayerSetup'
import GameBoard from './components/game/GameBoard'
import GameIntroScreen from './components/game/GameIntroScreen'
import WorldGeneratedScreen from './components/game/WorldGeneratedScreen'
import JoinGame from './pages/JoinGame'
import MyGames from './pages/MyGames'
import LanguageSwitcher from './components/ui/LanguageSwitcher'
import PhoneAuth from './components/auth/PhoneAuth'
import LiveRegions from './components/a11y/LiveRegions'
import Dialog from './components/ui/Dialog'
import { useAnnounce } from './hooks/useAnnounce'
import { useGameStore } from './store/gameStore'
import { gameAPI } from './services/api'
import { getApiUrl } from './utils/api-url'

function App() {
  const { t } = useTranslation()
  const [apiStatus, setApiStatus] = useState<string>('checking')
  const [currentRoute, setCurrentRoute] = useState<string>(window.location.pathname)
  const [showPhoneAuth, setShowPhoneAuth] = useState(false)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  // The sticky top bar can be collapsed to a small icon so it stops covering content on small
  // screens; the choice is remembered across reloads.
  const [headerCollapsed, setHeaderCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('rpg-header-collapsed') === '1'; } catch { return false; }
  })
  const collapseHeader = (v: boolean) => {
    setHeaderCollapsed(v);
    try { localStorage.setItem('rpg-header-collapsed', v ? '1' : '0'); } catch { /* ignore */ }
  }
  const [userData, setUserData] = useState<any>(null)
  const { currentGame, setupStep, error } = useGameStore()
  const announce = useAnnounce()

  // The whole app is one document that swaps its contents, so document.title never changed and
  // every "navigation" sounded identical. A screen reader reads the title on load and on
  // history moves; without this a user cannot tell My Games from an invitation from the board.
  useEffect(() => {
    const screen =
      currentRoute.startsWith('/join/') ? t('a11y.screenJoin')
      : currentRoute.startsWith('/intro/') ? t('a11y.screenIntro')
      : currentRoute.startsWith('/game/') ? t('a11y.screenGame')
      : currentRoute.startsWith('/games') ? t('nav.myGames')
      : t('a11y.screenHome');
    document.title = `${screen} — ${t('app.title')}`;
  }, [currentRoute, t])

  // Check authentication status
  useEffect(() => {
    const userDataStr = localStorage.getItem('userData');
    if (userDataStr) {
      try {
        setUserData(JSON.parse(userDataStr));
      } catch (e) {
        console.error('Failed to parse user data:', e);
      }
    }
  }, []);

  const handlePhoneAuthSuccess = (responseData: any) => {
    localStorage.setItem('userData', JSON.stringify(responseData));
    setUserData(responseData);
    setShowPhoneAuth(false);
    window.location.reload();
  };

  const doLogout = () => {
    setShowLogoutConfirm(false);
    localStorage.removeItem('userData');
    setUserData(null);

    // Dispatch custom event for other components
    window.dispatchEvent(new Event('userLogout'));

    // Small delay to let event propagate
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  // Errors were rendered as a banner two screens above the control that caused them, with no
  // live region — so for a screen reader a failed "Create game" was simply silence.
  useEffect(() => {
    if (error) announce(`${t('common.error')}: ${error}`, 'assertive');
  }, [error, announce, t])

  // Simple routing without react-router-dom
  useEffect(() => {
    const handleRouteChange = () => {
      setCurrentRoute(window.location.pathname);
    };

    window.addEventListener('popstate', handleRouteChange);
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, []);

  // Reconcile the persisted game with the backend on load.
  // - 404 (deleted server-side): reset so we don't render a stale "world created" screen.
  // - already generated (status !== 'setup') while sitting on the setup flow: reset so the
  //   home screen shows the landing instead of a stale "add players / generate world" screen
  //   (which 400s with "World already generated"). The game stays available in Saved games.
  useEffect(() => {
    const persistedGame = useGameStore.getState().currentGame;
    if (!persistedGame) return;

    const onSpecialRoute = ['/game/', '/intro/', '/join/'].some((p) =>
      window.location.pathname.startsWith(p)
    );

    gameAPI.getGame(persistedGame.id)
      .then((game: any) => {
        if (!onSpecialRoute && game?.status && game.status !== 'setup') {
          useGameStore.getState().reset();
        }
      })
      .catch((err) => {
        if (err?.response?.status === 404) {
          useGameStore.getState().reset();
          localStorage.removeItem(`game_${persistedGame.id}_player`);
        }
      });
    // Run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Check backend API connection
    const checkApi = () => {
      const apiUrl = getApiUrl();
      fetch(`${apiUrl}/api/health`)
        .then(res => res.json())
        .then(data => {
          setApiStatus('connected')
        })
        .catch(() => {
          setApiStatus('error')
        })
    };

    checkApi();
    // Retry every 5 seconds if not connected
    const interval = setInterval(() => {
      if (apiStatus !== 'connected') {
        checkApi();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [apiStatus])

  return (
    <div style={{ minHeight: '100vh', position: 'relative' }}>
      {/* The app's only two live regions — everything spoken goes through useAnnounce(). */}
      <LiveRegions />

      <a href="#main-content" className="skip-link">{t('a11y.skipToContent')}</a>

      {/* Animated Background */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 50% 50%, rgba(99, 102, 241, 0.1) 0%, transparent 50%)',
        pointerEvents: 'none',
        zIndex: 0,
      }} />

      {/* Collapsed header → a small floating button that brings the bar back */}
      {headerCollapsed && (
        <button
          onClick={() => collapseHeader(false)}
          aria-label={t('nav.expandHeader')}
          aria-expanded={false}
          aria-controls="app-header"
          title={t('nav.expandHeader')}
          style={{
            position: 'fixed',
            top: '0.6rem',
            right: '0.6rem',
            zIndex: 100,
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            border: '1px solid rgba(99, 102, 241, 0.4)',
            background: 'rgba(15, 12, 41, 0.85)',
            backdropFilter: 'blur(10px)',
            color: '#e2e8f0',
            fontSize: '1.3rem',
            cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
          }}
        >
          🎮
        </button>
      )}

      {/* Header */}
      {!headerCollapsed && (
      <header id="app-header" style={{
        padding: '1.5rem 2rem',
        background: 'rgba(15, 12, 41, 0.8)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(99, 102, 241, 0.2)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <a href="/" style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
            {/* Not an <h1>: the site name repeated on every screen competed with each screen's
                own heading, so navigating by headings turned up two or three "level 1"s and no
                structure. The page-level <h1> belongs to the content. */}
            <p style={{
              fontSize: '1.8rem',
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              margin: 0,
            }}>
              <span aria-hidden="true">🎮 </span>RPG Multiverse
            </p>
            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', color: '#a0aec0' }}>
              {t('app.subtitle')}
            </p>
          </a>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* Auth Button */}
            {userData?.user ? (
              <>
                <div style={{
                  display: 'none', // Hide phone number on mobile
                  '@media (minWidth: 768px)': { display: 'block' },
                  background: 'rgba(16, 185, 129, 0.15)',
                  border: '1px solid rgba(16, 185, 129, 0.4)',
                  borderRadius: '8px',
                  padding: '0.5rem 0.75rem',
                  color: '#10b981',
                  fontSize: '0.85rem',
                }}>
                  📱 {userData.user.phoneNumber?.replace(/(\d{1,2})(\d{3})(\d{3})(\d{2})(\d{2})/, '+$1 *** $5')}
                </div>
                <button
                  onClick={() => setShowLogoutConfirm(true)}
                  style={{
                    background: 'rgba(239, 68, 68, 0.15)',
                    border: '1px solid rgba(239, 68, 68, 0.4)',
                    borderRadius: '8px',
                    padding: '0.5rem 0.75rem',
                    color: '#ef4444',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('auth.logout')}
                </button>
              </>
            ) : (
              <button
                onClick={() => setShowPhoneAuth(true)}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.5rem 1rem',
                  color: '#fff',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  whiteSpace: 'nowrap',
                }}
              >
                📱 {t('auth.login')}
              </button>
            )}

            {/* Status Indicator */}
            <div role="status" aria-live="polite" aria-atomic="true" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              background: apiStatus === 'connected' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
              borderRadius: '20px',
              border: `1px solid ${apiStatus === 'connected' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
            }}>
              <div aria-hidden="true" style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: apiStatus === 'connected' ? '#10b981' : '#ef4444',
                animation: apiStatus === 'connected' ? 'glow 2s infinite' : 'none',
              }} />
              <span style={{ fontSize: '0.85rem', color: apiStatus === 'connected' ? '#10b981' : '#ef4444' }}>
                {apiStatus === 'connected'
                  ? t('common.online')
                  : apiStatus === 'error'
                    ? t('common.offline')
                    : t('common.loading')}
              </span>
            </div>

            {/* Collapse the whole bar to a small icon so it stops covering the view */}
            <button
              onClick={() => collapseHeader(true)}
              aria-label={t('nav.collapseHeader')}
              aria-expanded={true}
              aria-controls="app-header"
              title={t('nav.collapseHeader')}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                border: '1px solid rgba(99, 102, 241, 0.4)',
                background: 'rgba(99, 102, 241, 0.1)',
                color: '#c7d2fe',
                fontSize: '1.1rem',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              ▲
            </button>
          </div>
        </div>
      </header>
      )}

      {/* Main Content */}
      <main id="main-content" tabIndex={-1} style={{
        position: 'relative',
        zIndex: 1,
        outline: 'none',
        padding: (currentRoute.startsWith('/game/') || currentRoute.startsWith('/intro/')) ? '0' : '2rem',
      }}>
        {/* Route: Join Game */}
        {currentRoute.startsWith('/join/') && <JoinGame />}

        {/* Route: Game Intro */}
        {currentRoute.startsWith('/intro/') && (
          <GameIntroScreen
            gameId={currentRoute.split('/')[2]}
            onContinue={() => {
              const gameId = currentRoute.split('/')[2];
              // Get player ID from localStorage
              const playerId = localStorage.getItem(`game_${gameId}_player`);
              const url = playerId ? `/game/${gameId}?playerId=${playerId}` : `/game/${gameId}`;
              window.location.href = url;
            }}
          />
        )}

        {/* Route: Game Board */}
        {currentRoute.startsWith('/game/') && <GameBoard />}

        {/* Route: My Games */}
        {currentRoute.startsWith('/games') && <MyGames />}

        {/* Route: Home / Setup */}
        {!currentRoute.startsWith('/join/') && !currentRoute.startsWith('/game/') && !currentRoute.startsWith('/intro/') && !currentRoute.startsWith('/games') && (
          <>
            {error && (
              <div role="alert" style={{
                maxWidth: '600px',
                margin: '0 auto 2rem auto',
                padding: '1rem 1.5rem',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '12px',
                color: '#fca5a5',
                animation: 'fadeIn 0.3s ease-out',
              }}>
                <strong>{t('common.error')}:</strong> {error}
              </div>
            )}

            {setupStep === 'game-details' && !currentGame && <MagicalHome />}

            {currentGame && setupStep === 'add-players' && <PlayerSetup />}

            {currentGame && setupStep === 'generate-world' && (
          <WorldGeneratedScreen gameId={currentGame.id} />
        )}
          </>
        )}
      </main>

      {/* Phone Auth Modal */}
      {showPhoneAuth && (
        <PhoneAuth
          onSuccess={handlePhoneAuthSuccess}
          onCancel={() => setShowPhoneAuth(false)}
        />
      )}

      {/* Logout confirmation — guards against an accidental tap that would sign the user out.
          The markup had role="dialog" but none of the behaviour: focus stayed on the button
          behind the overlay, Escape did nothing, and the page underneath stayed readable. */}
      {showLogoutConfirm && (
        <Dialog
          title={t('auth.logoutConfirmTitle')}
          onClose={() => setShowLogoutConfirm(false)}
          maxWidth="360px"
        >
          <p style={{ margin: '0 0 1.25rem', color: '#cbd5e0', fontSize: '0.9rem', lineHeight: 1.5 }}>
            {t('auth.logoutConfirmMessage')}
          </p>
          <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => setShowLogoutConfirm(false)}
              style={{
                background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '8px', padding: '0.55rem 1rem', color: '#e2e8f0',
                fontSize: '0.9rem', cursor: 'pointer',
              }}
            >
              {t('auth.cancel')}
            </button>
            <button
              type="button"
              onClick={doLogout}
              style={{
                background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.5)',
                borderRadius: '8px', padding: '0.55rem 1rem', color: '#fca5a5',
                fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
              }}
            >
              {t('auth.logout')}
            </button>
          </div>
        </Dialog>
      )}
    </div>
  )
}

export default App
